import { FC, useEffect, useState } from 'react';
import { InputNumber } from 'antd';
import { TbArrowUpRight, TbNumbers } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { MiddleDot, PlanTag } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';

interface IdentifierCodesProps {
    filters: any;
    handleChange: any;
}

const IdentifierCodes: FC<IdentifierCodesProps> = ({ filters, handleChange }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, [filters]);

    const { hasMinimumPlan } = useEntitlements();

    const { token } = useAntDesignTheme();

    return !hasMinimumPlan('plus') ? (
        <div style={{ textAlign: 'center', margin: '2rem' }}>
            <PlanTag plan={'plus'} style={{ marginLeft: '0.5rem' }} />
        </div>
    ) : (
        <Form
            initialValues={filters?.identifierCodes}
            layout='vertical'
            onValuesChange={(changedValues, allValues) => {
                handleChange(['identifierCodes'], allValues);
            }}
        >
            <Form.Item
                label={
                    <>
                        <span style={{ color: token.colorTextTertiary }}>SIC</span>
                        <MiddleDot horizontalMargin={'0.25rem'} />
                        <a
                            href='https://www.sec.gov/search-filings/standard-industrial-classification-sic-code-list'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            SIC Code List
                            <TbArrowUpRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }} />
                        </a>
                    </>
                }
                name='sic'
            >
                <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    prefix={<TbNumbers style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                    value={filters?.identifierCodes?.sic}
                />
            </Form.Item>
            <Form.Item
                label={
                    <>
                        <span style={{ color: token.colorTextTertiary }}>CUSIP</span>
                        <MiddleDot horizontalMargin={'0.25rem'} />
                        <a
                            href='https://www.cusip.com/identifiers.html#/CUSIP'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            CUSIP Identifier
                            <TbArrowUpRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }} />
                        </a>
                    </>
                }
                name='cusip'
            >
                <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    prefix={<TbNumbers style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                    value={filters?.identifierCodes?.cusip}
                />
            </Form.Item>
            <Form.Item
                label={
                    <>
                        <span style={{ color: token.colorTextTertiary }}>LEI</span>
                        <MiddleDot horizontalMargin={'0.25rem'} />
                        <a href='https://www.cusip.com/identifiers.html#/LEI' target='_blank' rel='noopener noreferrer'>
                            LEI Identifier
                            <TbArrowUpRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }} />
                        </a>
                    </>
                }
                name='lei'
            >
                <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    prefix={<TbNumbers style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                    value={filters?.identifierCodes?.lei}
                />
            </Form.Item>
            <Form.Item
                label={
                    <>
                        <span style={{ color: token.colorTextTertiary }}>CEI</span>
                        <MiddleDot horizontalMargin={'0.25rem'} />
                        <a href='https://www.cusip.com/identifiers.html#/CEI' target='_blank' rel='noopener noreferrer'>
                            CEI Identifier
                            <TbArrowUpRight style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }} />
                        </a>
                    </>
                }
                name='cei'
            >
                <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    prefix={<TbNumbers style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                    value={filters?.identifierCodes?.cei}
                />
            </Form.Item>
            <Form.Item
                label={
                    <>
                        <span style={{ color: token.colorTextTertiary }}>ISIN</span>
                        <MiddleDot horizontalMargin={'0.25rem'} />
                        <a
                            href='https://www.cusip.com/identifiers.html#/ISIN'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            ISIN Identifier
                            <TbArrowUpRight
                                style={{
                                    verticalAlign: '-0.125rem',
                                    marginLeft: '0.25rem',
                                }}
                            />
                        </a>
                    </>
                }
                name='isin'
            >
                <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    prefix={<TbNumbers style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                    value={filters?.identifierCodes?.isin}
                />
            </Form.Item>
            <Form.Item
                label={
                    <>
                        <span style={{ color: token.colorTextTertiary }}>FIGI</span>
                        <MiddleDot horizontalMargin={'0.25rem'} />
                        <a href='https://www.openfigi.com/search' target='_blank' rel='noopener noreferrer'>
                            OpenFIGI Search
                            <TbArrowUpRight
                                style={{
                                    verticalAlign: '-0.125rem',
                                    marginLeft: '0.25rem',
                                }}
                            />
                        </a>
                    </>
                }
                name='figi'
            >
                <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    prefix={<TbNumbers style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                    value={filters?.identifierCodes?.figi}
                />
            </Form.Item>
        </Form>
    );
};

export default IdentifierCodes;
